import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Droplet, Shield, Sparkles, Heart, Award, Users, Star } from 'lucide-react';

const AboutPage = () => {
  return (
    <>
      <Helmet>
        <title>About Us - LuxuryStyle | Our Story</title>
        <meta 
          name="description" 
          content="Learn about LuxuryStyle's mission to create premium waterproof and tarnish-free jewellery. Our story, values, and commitment to quality." 
        />
        <meta property="og:title" content="About LuxuryStyle" />
        <meta property="og:description" content="Our mission: creating premium waterproof and tarnish-free jewellery for everyday elegance." />
        <link rel="canonical" href="https://luxurystyle.info/about" />
      </Helmet>

      <main data-testid="about-page" className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 md:py-32 overflow-hidden">
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.unsplash.com/photo-1737816473252-b7b8b60ae7b6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBnb2xkJTIwamV3ZWxyeSUyMGRhcmslMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc2ODgyOTQ4Nnww&ixlib=rb-4.1.0&q=85)'
            }}
          />
          <div className="absolute inset-0 bg-black/80" />
          <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
            <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
              Our Story
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-white mt-4 mb-6">
              About LuxuryStyle
            </h1>
            <div className="gold-divider mb-6" />
            <p className="text-white/70 text-lg max-w-2xl mx-auto">
              Crafting premium waterproof jewellery that combines timeless elegance 
              with modern durability.
            </p>
          </div>
        </section>

        {/* Our Mission */}
        <section className="py-20 md:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
                  Our Mission
                </span>
                <h2 className="text-3xl md:text-4xl font-medium text-white mt-4 mb-6">
                  Luxury Without Limits
                </h2>
                <div className="gold-divider-left mb-6" />
                <p className="text-white/60 leading-relaxed mb-6">
                  At LuxuryStyle, we believe that true luxury should fit seamlessly into your life. 
                  That's why we've dedicated ourselves to creating jewellery that doesn't just look 
                  beautiful—it stays beautiful, no matter where life takes you.
                </p>
                <p className="text-white/60 leading-relaxed mb-6">
                  Our pieces are crafted from premium 18K gold plated stainless steel, ensuring 
                  they remain waterproof and tarnish-free. Whether you're at the beach, in the 
                  shower, or hitting the gym, your LuxuryStyle jewellery maintains its stunning 
                  appearance.
                </p>
                <p className="text-white/60 leading-relaxed">
                  We're not just selling jewellery—we're offering a lifestyle. A lifestyle where 
                  you never have to choose between looking elegant and living freely.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1626122738142-bbd66f671eca?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxnb2xkJTIwYnJhY2VsZXQlMjB3b21hbiUyMHdyaXN0JTIwZGFyayUyMGZhc2hpb258ZW58MHx8fHwxNzY4ODI5NDg4fDA&ixlib=rb-4.1.0&q=85"
                  alt="LuxuryStyle jewellery collection"
                  className="w-full h-[500px] object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-4 border border-[#D4AF37]/30 pointer-events-none" />
              </div>
            </div>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-20 md:py-32 bg-[#0a0a0a]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
                What We Stand For
              </span>
              <h2 className="text-3xl md:text-4xl font-medium text-white mt-4 mb-4">
                Our Values
              </h2>
              <div className="gold-divider mb-6" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Quality */}
              <div className="section-card p-8 text-center group hover:-translate-y-1 transition-all duration-500">
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                  <Award className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-white mb-4">Uncompromising Quality</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Every piece undergoes rigorous quality control to ensure it meets our 
                  exacting standards for durability and beauty.
                </p>
              </div>

              {/* Innovation */}
              <div className="section-card p-8 text-center group hover:-translate-y-1 transition-all duration-500">
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                  <Sparkles className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-white mb-4">Continuous Innovation</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  We constantly push the boundaries of jewellery design and materials 
                  to bring you pieces that are both beautiful and practical.
                </p>
              </div>

              {/* Customer Focus */}
              <div className="section-card p-8 text-center group hover:-translate-y-1 transition-all duration-500">
                <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 flex items-center justify-center group-hover:border-[#D4AF37] transition-colors">
                  <Heart className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-medium text-white mb-4">Customer First</h3>
                <p className="text-white/60 text-sm leading-relaxed">
                  Your satisfaction is our priority. We're committed to providing 
                  exceptional service and support at every step.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The Difference */}
        <section className="py-20 md:py-32 bg-[#050505]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="text-center mb-16">
              <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
                Why Choose Us
              </span>
              <h2 className="text-3xl md:text-4xl font-medium text-white mt-4 mb-4">
                The LuxuryStyle Difference
              </h2>
              <div className="gold-divider mb-6" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="flex items-start space-x-4 p-6 bg-[#111] border border-white/5">
                <Droplet className="w-8 h-8 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Waterproof Design</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Our jewellery is designed to withstand water exposure. Swim, shower, or 
                    sweat—your pieces will maintain their shine and integrity.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 bg-[#111] border border-white/5">
                <Shield className="w-8 h-8 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Tarnish-Free Guarantee</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Say goodbye to green fingers and dull finishes. Our stainless steel 
                    construction ensures lasting brilliance without tarnishing.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 bg-[#111] border border-white/5">
                <Sparkles className="w-8 h-8 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">18K Gold Plating</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Each piece features premium 18K gold plating applied through an 
                    advanced process for maximum durability and beauty.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-6 bg-[#111] border border-white/5">
                <Users className="w-8 h-8 text-[#D4AF37] flex-shrink-0" strokeWidth={1.5} />
                <div>
                  <h3 className="text-lg font-medium text-white mb-2">Hypoallergenic Materials</h3>
                  <p className="text-white/60 text-sm leading-relaxed">
                    Our stainless steel base is hypoallergenic, making our jewellery 
                    perfect for those with sensitive skin.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Customer Reviews CTA Section */}
        <section className="py-16 md:py-24 bg-[#0a0a0a] border-t border-white/5">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <span className="text-[#D4AF37] font-accent text-xs uppercase tracking-[0.2em]">
              Our Customers
            </span>
            <h2 className="text-3xl md:text-4xl font-medium text-white mt-4 mb-4">
              Share Your Experience
            </h2>
            <div className="gold-divider mb-6" />
            <div className="flex justify-center mb-6">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-8 h-8 text-[#D4AF37] fill-[#D4AF37]" strokeWidth={1.5} />
                ))}
              </div>
            </div>
            <p className="text-white/60 text-lg mb-8 max-w-xl mx-auto">
              Your feedback means the world to us. If you've enjoyed wearing your LuxuryStyle jewellery, 
              we'd be honored if you'd share your experience with others.
            </p>
            <a
              href="https://share.google/jNF6BvocJeA38Cdry"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-google-review"
              className="btn-luxury inline-flex items-center space-x-2 text-lg px-8 py-4"
            >
              <Star className="w-5 h-5 fill-current" strokeWidth={1.5} />
              <span>Leave a Google Review</span>
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default AboutPage;
